<template>
    <div class="widget-template-category-widget-list w-full">
        <div class="w-full flex items-center mb-4">
            <RedesignBaseInput
                v-model="search"
                :placeholder="String($t('general.search'))"
                prefix-icon="Search"
                class="input-search w-full max-w-xs"
            />
        </div>
        <div class="w-full">
            <WidgetsAddLimitAlert
                v-if="isTotalWarning"
                type="warning"
                class="mb-2 lg:mb-4"
            >
                <span v-html="$t('widget.limit.warning')" />
            </WidgetsAddLimitAlert>
            <WidgetsAddLimitAlert
                v-else-if="isTotalError"
                type="error"
                class="mb-2 lg:mb-4"
            >
                <span v-html="$t('widget.limit.error')" />
            </WidgetsAddLimitAlert>
        </div>
        <div class="text-sm pb-2">
            <span v-html="$t('widget.add.clickToSelect')" />
        </div>
        <div class="template-category-widgets__list max-h-[386px] min-h-[200px] overflow-y-auto px-2 pt-3 -mx-2">
            <div class="w-full space-y-2">
                <WidgetTemplateItem
                    v-for="template in widgetFiltered"
                    :key="template.TemplateID"
                    :template="template"
                    :is-selected="isSelectedTemplate(template)"
                    :used-groups="widgetTemplatesInGroup[template.TemplateID || -1]"
                    @on-select="onTemplateSelect"
                    @on-preview="openPreviewModal"
                >
                    <template #counter>
                        <RedesignInputNumber
                            :value="getTemplateCount(template.TemplateID)"
                            :min-value="1"
                            :max-value="getMaxValueByTemplateId(template.TemplateID)"
                            @change="($evt) => onChangeTemplateCounter($evt, template.TemplateID)"
                        />
                    </template>
                </WidgetTemplateItem>
            </div>
        </div>
        <portal to="widget-modal-actions">
            <div class="flex items-center gap-2 justify-end">
                <RedesignBaseButton
                    type="soft"
                    class="min-w-30"
                    @click="onPrevStep"
                >
                    {{ $t('general.back') }}
                </RedesignBaseButton>
                <RedesignBaseButton
                    :disabled="nextDisabled"
                    class="min-w-30"
                    @click="onNextStep"
                >
                    {{ $t('general.next') }}
                </RedesignBaseButton>
            </div>
        </portal>
    </div>
</template>

<script setup lang="ts">
import WidgetTemplateItem from '@/components/RedesignComponents/Widgets/Create/WidgetTemplateItem.vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import WidgetsAddLimitAlert from '@/components/RedesignComponents/Widgets/Create/WidgetsAddLimitAlert.vue'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import RedesignInputNumber from '@/components/RedesignBaseComponents/Inputs/RedesignInputNumber.vue'
import { computed, ref } from 'vue'
import { MAX_WIDGETS_LIMIT, MAX_WIDGETS_WARNING } from '@/components/RedesignComponents/Widgets/widgets.enum'
import { IWidgetTemplateCategory, IWidgetTemplateListItem } from '@/types/templates'
import { debouncedWatch } from '@vueuse/core'
import i18n from '@/i18n'
import { ActiveDashboardData } from '@/composables/globalData/useDashoardsData'
import { TemplateHelp } from '@/types/template-help'

/* Props */
const props = defineProps<{
    category?: IWidgetTemplateCategory
    selectedTemplates?: Record<number, { count: number, template: IWidgetTemplateListItem }>
    totalWidgets?: number
}>()
/* Emits */
const emit = defineEmits<{
    (e: 'template:select', pl: IWidgetTemplateListItem): void,
    (e: 'template:count', val: number, templateId: number): void,
    (e: 'template:info', pl: TemplateHelp): void,
    (e: 'step:next'): void,
    (e: 'step:prev'): void
}>()

/* Data */
const search = ref<string>('')
const debouncedSearchModel = ref('')

/* Computed */
const isTotalWarning = computed(() => {
    return ((props.totalWidgets || 0) >= MAX_WIDGETS_WARNING) && !isTotalError.value
})
const isTotalError = computed(() => {
    return (props.totalWidgets || 0) >= MAX_WIDGETS_LIMIT
})
const nextDisabled = computed(() => {
    return !Object.keys(props.selectedTemplates || {}).length || (props.totalWidgets || 0) > MAX_WIDGETS_LIMIT + 1
})
const widgetFiltered = computed(() => {
    if (!debouncedSearchModel.value) {
        return sortWidgetList(props.category?.templates || [])
    }

    const _filteredList = props.category?.templates?.filter(template => {
        const _search = debouncedSearchModel.value.toLowerCase()
        const _string = (template.TemplateID?.toString() || '') + translateTemplateNameContentTag(template)
        return _string.toLowerCase().includes(_search)
    })
    return sortWidgetList(_filteredList)
})
const activeDashboardGroups = computed(() => {
    const dashboard = ActiveDashboardData.value || { WidgetGroupList: [] }
    return [ ...(dashboard.WidgetGroupList ?? []) ]
})
const widgetTemplatesInGroup = computed(() => {
    const result: Record<number, Array<string>> = {}
    activeDashboardGroups.value.forEach(group => {
        const groupName = group.WidgetGroupTitle || `Group ${group.WidgetGroupID}`
        const _templates = [ ...new Set(group.WidgetList.map(i => i.TemplateID)) ]
        _templates.forEach(templateID => {
            if (!result[templateID]) {
                result[templateID] = []
            }
            result[templateID].push(groupName)

        })
    })
    return result
})

/* Methods */
function translateTemplateNameContentTag (template: IWidgetTemplateListItem) {
    const _res = i18n.te(String(template.TemplateNameContentTag)) ? i18n.t(String(template.TemplateNameContentTag)) : template.TemplateName
    return String(_res)
}

function sortWidgetList (list: Array<IWidgetTemplateListItem> = []) {
    return [ ...list ].sort((a, b) => (a.CategoryOrder || 0) - (b.CategoryOrder || 0))
}

function isSelectedTemplate (widgetTemplate: IWidgetTemplateListItem) {
    return !!(props.selectedTemplates && props.selectedTemplates[widgetTemplate.TemplateID || -1])
}

function onTemplateSelect (template: IWidgetTemplateListItem) {
    emit('template:select', template)
}

function openPreviewModal (templateInfo: TemplateHelp) {
    emit('template:info', templateInfo)
}

function getTemplateCount (templateID: number) {
    if (!props.selectedTemplates) {
        return 0
    }
    return props.selectedTemplates[templateID]?.count
}

function getMaxValueByTemplateId (templateId: number) {
    if (!props.selectedTemplates) {
        return
    }
    const _diff = MAX_WIDGETS_LIMIT - (props.totalWidgets || 0)
    const _current = props.selectedTemplates[templateId]?.count || 0

    return Math.min(_current + _diff, MAX_WIDGETS_LIMIT)
}

function onChangeTemplateCounter (val: number, templateId: number) {
    emit('template:count', val, templateId)
}

function onPrevStep () {
    emit('step:prev')
}

function onNextStep () {
    emit('step:next')
}

/* Watcher */
debouncedWatch(
    search,
    (val: string) => {
        debouncedSearchModel.value = val
    },
    { debounce: 300 }
)
</script>
